import {
  Button,
  Modal,
  OutlinedSelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import { SelectCreateable } from '../../../../../../components/form/select/SelectCreateable';
import { useValidations } from '../../../../../../hooks/useValidations';
import {
  CurriculumAdmissionRequirementFormValue,
  CurriculumRequirementOption,
} from '../../../../../../types/CurriculumTypes';
import { isNumber } from '../../../../../../utils/math';

type AddAdmissionRequirementModalProps = {
  show: boolean;
  onClose: () => void;
  options: CurriculumRequirementOption[];
  onAdd: (requirement: CurriculumAdmissionRequirementFormValue) => void;
};

type FormValues = {
  requirementName: OutlinedSelectOptionType | null;
  requirementCode: string;
  requirementDescription: string;
};

const DEFAULT_VALUES: FormValues = {
  requirementName: null,
  requirementCode: '',
  requirementDescription: '',
};

const findCurrentRequirement = (
  requirements: CurriculumRequirementOption[],
  requirementId: number,
) => {
  return requirements.find((requirement) => requirement.id === requirementId);
};

export const AddAdmissionRequirementModal = ({
  show,
  onClose,
  options,
  onAdd,
}: AddAdmissionRequirementModalProps) => {
  const { t } = useTranslation();
  const { msgValidations } = useValidations();
  const prefix = 'studyPlans.curriculum.admissionRequirements.modal';

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    shouldUnregister: false,
  });

  const [disableFormFields, setDisableFormFields] = useState(true);
  const { control, handleSubmit, reset, setValue, watch } = methods;
  const [selectedRequirement] = watch(['requirementName']);

  const requirementOptions = useMemo(() => {
    return options.map(({ id, name, code }) => ({
      label: `${name} ${code}`,
      value: id,
    }));
  }, [options]);

  useEffect(() => {
    if (show) {
      reset(DEFAULT_VALUES);
      setDisableFormFields(true);
    }
  }, [show, reset]);

  useEffect(() => {
    if (!selectedRequirement) {
      return;
    }

    if (!selectedRequirement.__isNew__) {
      const currentRequirement = findCurrentRequirement(
        options,
        Number(selectedRequirement.value),
      );

      setValue('requirementCode', currentRequirement?.code || '');
      setValue('requirementDescription', currentRequirement?.description || '');
      setDisableFormFields(true);
      return;
    }

    setDisableFormFields(false);
    setValue('requirementCode', '');
    setValue('requirementDescription', '');
  }, [options, selectedRequirement, setValue]);

  const handleClear = useCallback(() => {
    reset(DEFAULT_VALUES);
    setDisableFormFields(true);
  }, [reset]);

  const handleAdd = (values: FormValues) => {
    if (!values.requirementName) {
      return;
    }

    onAdd({
      id:
        values.requirementName && isNumber(values.requirementName.value)
          ? Number(values.requirementName.value)
          : undefined,
      name: values.requirementName.label,
      code: values.requirementCode,
      description: values.requirementDescription,
    });
    onClose();
  };

  return (
    <Modal isOpen={show} toggle={onClose} size="lg">
      <FormProvider {...methods}>
        <Container>
          <Row>
            <Col xs={12} className="text-center mb-4">
              <h2 className="text-dark fs-22 mb-0">{t(`${prefix}.title`)}</h2>
              <p className="fs-16 mt-2 mb-0">{t(`${prefix}.description`)}</p>
            </Col>
            <Col xs={12} className="pb-3">
              <SelectCreateable
                control={control}
                name="requirementName"
                options={requirementOptions}
                label={t(`${prefix}.nameLabel`)}
                placeholder={t(`${prefix}.nameLabel`)}
                rules={{ required: msgValidations.required }}
              />
            </Col>
            <Col xs={12} className="pb-3">
              <TextInput
                control={control}
                name="requirementCode"
                label={t(`${prefix}.codeLabel`)}
                readOnly={disableFormFields}
                rules={{ required: msgValidations.required }}
              />
            </Col>
            <Col xs={12} className="pb-3">
              <TextAreaInput
                control={control}
                name="requirementDescription"
                label={t(`${prefix}.descriptionLabel`)}
                readOnly={disableFormFields}
                rules={{ required: msgValidations.required }}
                maxLength={255}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Button
                text={t('common.actions.cancel')}
                type="button"
                fullwidth
                outlined
                onClick={() => {
                  onClose();
                  handleClear();
                }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Button
                text={t('common.actions.add')}
                type="button"
                fullwidth
                onClick={() => handleSubmit(handleAdd)()}
              />
            </Col>
          </Row>
        </Container>
      </FormProvider>
    </Modal>
  );
};
